import { ArrowLeft, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Disclosure, StatusGlyph, StatusPill } from '@/components/surface/ui';
import { cn } from '@/lib/utils';
import { isWorking, runIcon, runTone, targetIcon, targetTone } from '@/modules/automation/status';
import type { RunStatus, TargetStatus } from '@/types/api';

/**
 * The small visual vocabulary Automation shares. Components only; the rules live in status.ts.
 */

export function PageHeader({
  title,
  description,
  actions,
  back,
  eyebrow,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  back?: { to: string; label: string } | undefined;
  eyebrow?: ReactNode;
}) {
  return (
    <header className="space-y-3">
      {back !== undefined && (
        <Link
          to={back.to}
          className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          {back.label}
        </Link>
      )}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          {eyebrow !== undefined && <div className="mb-1.5 flex flex-wrap items-center gap-2">{eyebrow}</div>}
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {description !== undefined && <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{description}</p>}
        </div>
        {/* ml-auto keeps the actions at the right when a long description pushes them onto a line
            of their own, rather than dropping them at the left under the text. */}
        {actions !== undefined && <div className="ml-auto flex flex-wrap items-center justify-end gap-2">{actions}</div>}
      </div>
    </header>
  );
}

/** What a playbook or template checks and leaves alone, folded away under the short description. */
export function TechnicalDetails({ details }: { details: string[] }) {
  if (details.length === 0) return null;

  return (
    <Disclosure title="Technical details">
      <ul className="list-disc space-y-1.5 pl-10 pr-5 text-[13px] leading-6 text-foreground/85 marker:text-muted-foreground">
        {details.map((detail) => (
          <li key={detail}>{detail}</li>
        ))}
      </ul>
    </Disclosure>
  );
}

export function RunStatusPill({ status, label }: { status: RunStatus; label: string }) {
  return <StatusPill tone={runTone[status]} label={label} pulse={isWorking(status) || status === 'queued'} />;
}

export function RunStatusGlyph({ status, label, size = 'md' }: { status: RunStatus; label: string; size?: 'sm' | 'md' | 'lg' }) {
  return <StatusGlyph icon={runIcon[status]} tone={runTone[status]} spin={isWorking(status)} label={label} size={size} />;
}

export function TargetStatusGlyph({ status, label, size = 'sm' }: { status: TargetStatus; label: string; size?: 'sm' | 'md' }) {
  return <StatusGlyph icon={targetIcon[status]} tone={targetTone[status]} spin={isWorking(status)} label={label} size={size} />;
}

/** A labelled field with room for help and an error, the same everywhere in Automation. */
export function Field({
  label,
  htmlFor,
  help,
  error,
  children,
  className,
}: {
  label: string;
  htmlFor?: string | undefined;
  help?: ReactNode;
  error?: string | undefined;
  children: ReactNode;
  className?: string | undefined;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={htmlFor} className="text-[13px] font-medium leading-none text-foreground">
        {label}
      </label>
      {children}
      {error !== undefined && error !== '' ? (
        <p className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : (
        help !== undefined && help !== '' && <p className="text-xs text-muted-foreground">{help}</p>
      )}
    </div>
  );
}

/** A two-state control that reads as a switch, with its label beside it. */
export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  id,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: ReactNode;
  disabled?: boolean;
  id?: string;
}) {
  return (
    <div className={cn('flex items-start gap-3', disabled && 'opacity-60')}>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          checked ? 'bg-primary' : 'bg-slate-900/[0.15] dark:bg-white/[0.15]',
          disabled && 'cursor-not-allowed',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'inline-block size-4 rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-[18px]' : 'translate-x-0.5',
          )}
        />
      </button>
      <label htmlFor={id} className="min-w-0 cursor-pointer select-none">
        <span className="block text-[13px] font-medium leading-5">{label}</span>
        {description !== undefined && <span className="block text-xs text-muted-foreground">{description}</span>}
      </label>
    </div>
  );
}

/** One of a small set of options, drawn as a segmented control. */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string; icon?: LucideIcon; disabled?: boolean }[];
  ariaLabel: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="inline-flex flex-wrap gap-1 rounded-lg bg-slate-900/[0.05] p-1 dark:bg-white/[0.05]"
    >
      {options.map((option) => {
        const selected = option.value === value;

        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={option.disabled}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              selected
                ? 'bg-card text-foreground shadow-sm ring-1 ring-slate-900/[0.08] dark:ring-white/[0.08]'
                : 'text-muted-foreground hover:text-foreground',
              option.disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            {option.icon && <option.icon className="size-3.5" aria-hidden />}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** A selectable chip for multi-choice values: extensions, operating systems. */
export function Chip({
  selected,
  onClick,
  children,
  disabled = false,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'inline-flex items-center rounded-md px-2.5 py-1 text-[13px] transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected
          ? 'bg-primary/[0.12] font-medium text-primary ring-1 ring-primary/40 dark:bg-primary/[0.16]'
          : 'bg-slate-900/[0.05] text-foreground/80 hover:bg-slate-900/[0.09] dark:bg-white/[0.05] dark:hover:bg-white/[0.09]',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      {children}
    </button>
  );
}
